import { QueryClient, onlineManager } from "@tanstack/react-query";

let resumingFromRateLimitPause = false;

export const ohQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // prevent refetch on window / tab refocus
      refetchOnWindowFocus: false,
      // prevent retry if a request fail
      retry: false,
      // remove query data from catch once a hook is unmount
      // keep react-query behavior iso to hb current expected behavior
      gcTime: 0,
      // suppress the blanket stale-refetch when the rate-limit pause ends;
      // real reconnects keep the standard behavior
      refetchOnReconnect: () => !resumingFromRateLimitPause,
    },
  },
});

// ------------------------------------------
// ---- Global pause on network requests ----
// ------------------------------------------
// Backpressure for the OH API rate limit (Traefik, bucket keyed on the
// Authorization header): on a 429, stop every OH request for a beat instead
// of letting the fan-out re-form the spike. Single trigger: the API client's
// response hook (services_v2/index.ts) — downstream, the 429 is only a
// header-less FETCH_ERROR nothing can recognise.

// The 429's retry hints are never JS-readable (cross-origin, no
// Access-Control-Expose-Headers), so this fixed pause is the normal case.
export const DEFAULT_RATE_LIMIT_PAUSE_MS = 2000;

let globalPauseTimer: NodeJS.Timeout | null = null;

// "x-retry-in" (precise) wins over "retry-after" (rounded seconds); fall back
// when the hint is absent or unparseable (e.g. an HTTP-date Retry-After).
export const rateLimitDelayFromHeaders = (headers?: Headers | null): number => {
  const xRetryIn = headers?.get("x-retry-in");
  if (xRetryIn) {
    const delayMs = (xRetryIn.endsWith("ms") ? 1 : 1000) * parseInt(xRetryIn, 10);
    if (Number.isFinite(delayMs) && delayMs > 0) return delayMs;
  }
  const retryAfter = headers?.get("retry-after");
  if (retryAfter) {
    const delayMs = 1000 * parseInt(retryAfter, 10);
    if (Number.isFinite(delayMs) && delayMs > 0) return delayMs;
  }
  return DEFAULT_RATE_LIMIT_PAUSE_MS;
};

/**
 * Pause ALL OH network requests for `delayMs`, then resume and relaunch what
 * the pause interrupted. Repeated calls reset the timer.
 */
export const pauseOhQueries = (delayMs: number = DEFAULT_RATE_LIMIT_PAUSE_MS): void => {
  if (globalPauseTimer) clearTimeout(globalPauseTimer);

  onlineManager.setOnline(false);
  // reverts in-flight queries to "pending", nothing left in flight
  ohQueryClient.cancelQueries();

  globalPauseTimer = setTimeout(() => {
    globalPauseTimer = null;

    // setOnline notifies synchronously, so the flag can be scoped to this call
    resumingFromRateLimitPause = true;
    onlineManager.setOnline(true);
    resumingFromRateLimitPause = false;

    // relaunch the reverted queries — nothing else would (retry and
    // refetchOnWindowFocus are off); cancelRefetch: false leaves fetches
    // already restarted by setOnline alone
    ohQueryClient.refetchQueries(
      { type: "active", predicate: query => query.state.status === "pending" },
      { cancelRefetch: false }
    );
  }, delayMs);
};
